import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const CartPage = ({ language }) => {
  const [cart, setCart] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    loadCart();
  }, []);

  const loadCart = () => {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
  };

  const updateQuantity = (productId, newQuantity) => {
    if (newQuantity < 1) return;
    
    const newCart = cart.map(item =>
      item.id === productId ? { ...item, quantity: newQuantity } : item
    );
    setCart(newCart);
    localStorage.setItem('cart', JSON.stringify(newCart));
  };

  const removeItem = (productId) => {
    const newCart = cart.filter(item => item.id !== productId);
    setCart(newCart);
    localStorage.setItem('cart', JSON.stringify(newCart));
  };

  const getTotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const proceedToCheckout = () => {
    if (cart.length === 0) {
      alert(language === 'en' ? 'Your cart is empty!' : 'உங்கள் கூடை காலியாக உள்ளது!');
      return;
    }
    navigate('/checkout');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-orange-50 py-12 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-green-800 mb-4">
            {language === 'en' ? 'Shopping Cart' : 'வாங்குதல் கூடை'}
          </h1>
          <button
            onClick={() => navigate('/shop')}
            className="text-green-600 hover:text-green-700 font-semibold flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            {language === 'en' ? 'Continue Shopping' : 'வாங்குதலை தொடரவும்'}
          </button>
        </div>

        {cart.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <svg className="w-24 h-24 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <h2 className="text-2xl font-bold text-gray-600 mb-2">
              {language === 'en' ? 'Your cart is empty' : 'உங்கள் கூடை காலியாக உள்ளது'}
            </h2>
            <p className="text-gray-500 mb-6">
              {language === 'en' 
                ? 'Add some items to get started'
                : 'தொடங்க சில பொருட்களைச் சேர்க்கவும்'}
            </p>
            <button
              onClick={() => navigate('/shop')}
              className="bg-green-600 text-white px-8 py-3 rounded-lg hover:bg-green-700 transition font-semibold"
            >
              {language === 'en' ? 'Start Shopping' : 'வாங்குதல் தொடங்கவும்'}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow-md">
                {cart.map(item => (
                  <div
                    key={item.id}
                    className="flex items-center gap-4 p-6 border-b last:border-b-0 hover:bg-gray-50 transition"
                  >
                    {/* Product Image */}
                    <img
                      src={item.image_url || 'https://via.placeholder.com/100'}
                      alt={language === 'en' ? item.name : item.name_ta}
                      className="w-24 h-24 object-cover rounded-lg"
                    />

                    {/* Product Info */}
                    <div className="flex-1">
                      <h3 className="font-bold text-lg text-gray-800 mb-1">
                        {language === 'en' ? item.name : item.name_ta}
                      </h3>
                      <p className="text-sm text-gray-600 mb-2 line-clamp-1">
                        {language === 'en' ? item.description : item.description_ta}
                      </p>
                      <p className="text-green-600 font-bold">₹{item.price}</p>
                    </div>

                    {/* Quantity Controls */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        className="w-8 h-8 bg-gray-200 rounded-full hover:bg-gray-300 transition flex items-center justify-center font-bold"
                      >
                        -
                      </button>
                      <span className="w-12 text-center font-bold">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className="w-8 h-8 bg-gray-200 rounded-full hover:bg-gray-300 transition flex items-center justify-center font-bold"
                      >
                        +
                      </button>
                    </div>

                    {/* Subtotal & Remove */}
                    <div className="text-right">
                      <p className="font-bold text-lg text-gray-800 mb-2">
                        ₹{item.price * item.quantity}
                      </p>
                      <button
                        onClick={() => removeItem(item.id)}
                        className="text-red-600 hover:text-red-700 text-sm font-semibold"
                      >
                        {language === 'en' ? 'Remove' : 'அகற்று'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-md p-6 sticky top-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-6">
                  {language === 'en' ? 'Order Summary' : 'ஆர்டர் சுருக்கம்'}
                </h2>

                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-gray-600">
                    <span>{language === 'en' ? 'Subtotal' : 'மொத்தம்'}:</span>
                    <span className="font-semibold">₹{getTotal()}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>{language === 'en' ? 'Shipping' : 'ஷிப்பிங்'}:</span>
                    <span className="font-semibold text-green-600">
                      {language === 'en' ? 'Free' : 'இலவசம்'}
                    </span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>{language === 'en' ? 'Tax (GST 18%)' : 'வரி (GST 18%)'}:</span>
                    <span className="font-semibold">₹{Math.round(getTotal() * 0.18)}</span>
                  </div>
                  <div className="border-t pt-3 flex justify-between text-xl font-bold text-gray-800">
                    <span>{language === 'en' ? 'Total' : 'மொத்தம்'}:</span>
                    <span className="text-green-600">₹{Math.round(getTotal() * 1.18)}</span>
                  </div>
                </div>

                <button
                  onClick={proceedToCheckout}
                  className="w-full bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 transition font-bold text-lg"
                >
                  {language === 'en' ? 'Proceed to Checkout' : 'செக்அவுட்டுக்கு செல்லவும்'}
                </button>

                <div className="mt-4 text-center text-sm text-gray-500">
                  {language === 'en' 
                    ? 'Secure checkout powered by Razorpay'
                    : 'Razorpay மூலம் பாதுகாப்பான செக்அவுட்'}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CartPage;
